/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";
import { FormattedNumber } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { calculateOrderAmount } from "../../state/actions/orderActions";
import { RootState } from "../../state/store/store";

export interface OrderSummaryInfoProps {
  totalPayment: any;
  subtotal: any;
  chargeItems: any[];
  discountItems: any[];
  vendorId?: number;
  readonly?: boolean;
}

// eslint-disable-next-line react/display-name
export default (props: OrderSummaryInfoProps) => {
  const dispatch = useDispatch();
  const [couponCode, setCouponCode] = useState("");

  const orderState = useSelector((state: RootState) => state.order);
  const appliedCoupon = orderState.appliedCoupon || "";
  const calculationWarnings = orderState.warnings || [];
  const isRecalculatingOrderAmount =
    orderState.orderAmount.fetchStatus === "fetching";

  const handleApplyCoupon = () => {
    if (!couponCode.trim()) {
      return;
    }
    if (couponCode.trim().toUpperCase() === appliedCoupon && props.vendorId) {
      // Same code re-submitted, recalculate directly to avoid clearing warnings
      // without retriggering calculateOrderAmount via the useEffect dependency.
      dispatch(calculateOrderAmount(props.vendorId, orderState));
      return;
    }
    dispatch({ type: "SET_ORDER_COUPON", payload: { couponCode: couponCode } });
  };

  const handleRemoveCoupon = () => {
    setCouponCode("");
    dispatch({ type: "SET_ORDER_COUPON", payload: { couponCode: "" } });
  };

  const isDataLoaded = props.subtotal && props.subtotal.OrderItem;

  return (
    <div className="OrderSummaryInfo info-box info-bottom">
      <div className="payment-section">
        {isDataLoaded ? (
          <table className="payment-table">
            <tbody>
              <tr>
                <td>Subtotal:</td>
                <td>
                  {props.subtotal?.OrderItem?.currencySymbol}
                  <FormattedNumber
                    minimumFractionDigits={2}
                    value={props.subtotal?.OrderItem?.amount || 0}
                  />
                </td>
                <td></td>
              </tr>

              {!props.readonly &&
                (appliedCoupon.length === 0 ||
                  calculationWarnings.length > 0) && (
                  <tr className="promo-input-card-row">
                    <td colSpan={3}>
                      <div className="promo-card-box">
                        {calculationWarnings.map((warning, index) => (
                          <div
                            className="invalid-coupon text-danger"
                            key={`warning-${index}`}
                          >
                            {warning.message}
                          </div>
                        ))}

                        <div className="promo-section-integrated">
                          <div className="promo-input-group">
                            <input
                              type="text"
                              aria-label="Coupon code"
                              placeholder="Got a coupon code?"
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleApplyCoupon();
                                }
                              }}
                              onBlur={() => {
                                handleApplyCoupon();
                              }}
                            />
                            <button
                              aria-label="Apply coupon"
                              type="button"
                              onClick={handleApplyCoupon}
                              disabled={
                                couponCode.trim() === "" ||
                                isRecalculatingOrderAmount
                              }
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}

              {props.discountItems &&
                props.discountItems.length > 0 &&
                props.discountItems.map((item) => (
                  <tr className="applied-coupon-row" key={item.name}>
                    <td className="coupon-title-cell">{item.name}</td>
                    <td className="discount">
                      {item.price?.currencySymbol}
                      <FormattedNumber
                        minimumFractionDigits={2}
                        value={item.price?.amount || 0}
                      />
                    </td>
                    <td className="coupon-action-cell">
                      {!props.readonly && (
                        <button
                          type="button"
                          onClick={handleRemoveCoupon}
                          className="coupon-row-close-btn"
                          aria-label="Remove coupon"
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3.5"
                          >
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}

              {props.chargeItems.map((item) => (
                <tr key={item.name}>
                  <td>{item.name}:</td>
                  <td>
                    {item.price?.currencySymbol}
                    <FormattedNumber
                      minimumFractionDigits={2}
                      value={item.price?.amount || 0}
                    />
                  </td>
                  <td></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>

      <div>
        Grand Total: &nbsp;
        <strong>
          {props.totalPayment?.currencySymbol}
          <FormattedNumber
            minimumFractionDigits={2}
            value={props.totalPayment?.amount || 0}
          />
        </strong>
      </div>
    </div>
  );
};
